import React, { useState } from 'react';
import {
  Alert,
  Box,
  Container,
  ExpandableSection,
  Header,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import {
  CONCERN_ROLE, HUMAN_VERIFY_ROLE, advisoryDetails, groupCategoryChecks, isHumanVerification,
} from './reviewRecord';
import {
  CheckRow, Marked, NOT_APPLICABLE_TEXT, categoryStatus, checkName, checksNotSavedText,
} from './checkDisplay';
import { dashboardIds, howToVerify, verifyItems } from './humanVerification';
import { isLinkTarget } from './navigation';

/**
 * One detail-page tab per ISFR review category, each listing every check in
 * the category with its result, details and cited quotes, grouped by what the
 * reviewer has to do with it. Human verification lists only what the reviewer
 * does by hand, each with why and how. The concern tabs list passed checks
 * first, then what failed or did not complete, then the flags; each group folds,
 * colored by its state, and the flags start folded with the checks inside named.
 * The categories and their labels come from the record's category rollup, in
 * review order (human verification first), so a category the agent adds gets a
 * tab without a frontend change.
 */

export const categoryTabId = (key) => `isfr-category-${key}`;

// The human verification items are the reviewer's own to complete: nothing is
// waiting on another person or team.
const roleDescription = (category) => {
  if (category.advisory) {
    return 'Yours to verify. The agent\'s review here assists you and is not a result, so this stays open until you verify it yourself.';
  }
  if (category.role === HUMAN_VERIFY_ROLE) {
    return 'Yours to verify: you check each one yourself. While any is open, the request cannot be approved.';
  }
  return 'Judged on its own. If it is not clear, the request cannot be approved.';
};

// The order a tab lists its groups in, and how each shows. On the concern tabs
// every group can fold: passed and failed checks start open, since the reviewer
// reads both; the flags, and what does not apply, start folded, to open if wanted.
const REVIEW_ORDER = ['failed', 'other', 'incomplete', 'assisting', 'flagged', 'passed', 'notApplicable'];
const CONCERN_ORDER = ['passed', 'notApplicable', 'failed', 'other', 'incomplete', 'flagged'];
const CONCERN_FOLDED = new Set(['notApplicable', 'flagged']);
const layoutOf = (category) => {
  if (category.role === CONCERN_ROLE && !category.advisory) {
    return { order: CONCERN_ORDER, collapsible: true, folded: CONCERN_FOLDED };
  }
  return { order: REVIEW_ORDER };
};

// A folding group's header, colored by its state. Folded, it names the checks
// inside and says how to see them.
const FoldingGroup = ({
  title, description, indicator, checks, folded, children,
}) => {
  // A link to one of its checks opens it, so the link lands on the check.
  const [open, setOpen] = useState(() => !folded || checks.some((c) => isLinkTarget(c.id)));
  const closed = (
    <>
      {description && <Box variant="small" display="block" color="text-body-secondary">{description}</Box>}
      <Box variant="small" display="block" color="text-body-secondary">
        {`${checks.map(checkName).join(' · ')}. Unfold to see full details.`}
      </Box>
    </>
  );
  return (
    <div data-testid="isfr-category-group">
      <ExpandableSection
        variant="container"
        expanded={open}
        onChange={({ detail }) => setOpen(detail.expanded)}
        headerText={indicator ? (
          <StatusIndicator type={indicator}>
            <Box variant="span" fontSize="heading-m" fontWeight="bold" color="inherit">{title}</Box>
          </StatusIndicator>
        ) : title}
        headerCounter={`(${checks.length})`}
        headerDescription={open ? description : closed}
      >
        {children}
      </ExpandableSection>
    </div>
  );
};

const Group = ({
  title, description, indicator, checks, role, advisory, statusText, nameOf, reasonShown, collapsible, folded,
}) => {
  if (checks.length === 0) return null;
  const body = (
    <SpaceBetween size="l">
      {checks.map((check) => (
        <CheckRow
          key={check.id}
          check={check}
          role={role}
          advisory={advisory}
          statusText={statusText}
          nameOf={nameOf}
          reasonShown={reasonShown}
        />
      ))}
    </SpaceBetween>
  );
  if (collapsible) {
    return (
      <FoldingGroup title={title} description={description} indicator={indicator} checks={checks} folded={folded}>
        {body}
      </FoldingGroup>
    );
  }
  return (
    <div data-testid="isfr-category-group">
      <Container header={<Header variant="h3" description={description}>{title}</Header>}>{body}</Container>
    </div>
  );
};

// Human verification, bare: each item the reviewer does by hand, why, and how.
const VerifyList = ({ category, checks, request }) => {
  const items = verifyItems(category, checks.byId);
  const ids = dashboardIds(request);
  return (
    <Container
      header={<Header variant="h3" description="Check these yourself before you approve.">To verify by hand</Header>}
    >
      {items.length === 0 ? <Box>Nothing to verify by hand.</Box> : (
        <SpaceBetween size="m">
          {items.map((check) => {
            const reason = check.coverage && check.coverage.verify_reason;
            const how = howToVerify(check, ids);
            return (
              <div key={check.id} data-testid="isfr-verify-item">
                <StatusIndicator type="pending">
                  <Box variant="strong" display="inline">{checkName(check)}</Box>
                  {reason && reason.label ? ` · ${reason.label}` : ''}
                </StatusIndicator>
                {how.map((line, i) => (
                  <Box key={i} padding={{ left: 'l', top: 'xxs' }} color="text-body-secondary">
                    <Marked text={line} />
                  </Box>
                ))}
              </div>
            );
          })}
        </SpaceBetween>
      )}
    </Container>
  );
};

export const CategoryTab = ({ category, checks, request }) => {
  const status = categoryStatus(category);
  const details = checks.status === 'ok' ? advisoryDetails(category, checks.byId) : [];
  const header = (
    <Container header={<Header variant="h2" description={roleDescription(category)}>{category.label}</Header>}>
      <StatusIndicator type={status.type}>{status.text}</StatusIndicator>
      {details.length > 0 && (
        <div data-testid="isfr-category-details">
          {details.map((detail, i) => (
            <Box key={i} padding={{ top: 'xxs' }}><StatusIndicator type="warning"><Marked text={detail} /></StatusIndicator></Box>
          ))}
        </div>
      )}
    </Container>
  );
  if (checks.status !== 'ok') {
    return (
      <SpaceBetween size="l">
        {header}
        <Alert type="info" header="Check details are not on this record">
          {`${checksNotSavedText(checks.status)} Until then, this tab can show only the category state.`}
        </Alert>
      </SpaceBetween>
    );
  }
  if (isHumanVerification(category)) {
    return (
      <SpaceBetween size="l">
        {header}
        <VerifyList category={category} checks={checks} request={request} />
      </SpaceBetween>
    );
  }
  const groups = groupCategoryChecks(category, checks.byId);
  const humanVerify = category.role === HUMAN_VERIFY_ROLE;
  const layout = layoutOf(category);
  const nameOf = (id) => (checks.byId.has(id) ? checkName(checks.byId.get(id)) : 'A check not shown on this record');
  const byKey = {
    failed: { title: 'Failed', indicator: 'error', checks: groups.failed },
    other: { title: 'Other results', indicator: 'info', checks: groups.other },
    incomplete: {
      title: humanVerify ? 'To verify by hand' : 'Not fully reviewed',
      indicator: humanVerify ? 'pending' : 'warning',
      description: (() => {
        if (category.advisory) return 'The agent\'s review could not run here, so you verify this yourself.';
        return humanVerify
          ? 'The agent cannot run these yet. Check each one yourself.'
          : 'These did not complete, so they do not count as passes.';
      })(),
      checks: groups.incomplete,
      // The header already says why the agent's review could not run.
      reasonShown: details.length > 0,
    },
    assisting: {
      title: 'The agent\'s review',
      indicator: 'info',
      description: 'Assists your verification. It is not a pass or a finding; you verify the item yourself.',
      checks: groups.assisting,
      advisory: true,
    },
    flagged: {
      title: 'Flagged',
      indicator: 'warning',
      description: 'These did not affect the verdict. Keep them in mind.',
      checks: groups.flagged,
    },
    passed: { title: 'Passed', indicator: 'success', checks: groups.passed },
    notApplicable: {
      title: NOT_APPLICABLE_TEXT,
      indicator: 'stopped',
      description: 'Recorded, but not counted for a request at this tier.',
      checks: groups.notApplicable,
      statusText: NOT_APPLICABLE_TEXT,
    },
  };
  return (
    <SpaceBetween size="l">
      {header}
      {layout.order.map((key) => (
        <Group
          key={key}
          {...byKey[key]}
          role={category.role}
          nameOf={nameOf}
          collapsible={layout.collapsible}
          folded={Boolean(layout.folded && layout.folded.has(key))}
        />
      ))}
      {groups.missing.length > 0 && (
        <Alert type="warning" header="Checks with no stored result">
          {`The category lists ${groups.missing.length === 1 ? 'a check' : `${groups.missing.length} checks`} the record has no result for.`}
        </Alert>
      )}
    </SpaceBetween>
  );
};
